/**
 * @file Fenwick Tree (Binary Indexed Tree) implementation and React hook.
 * @file Fenwick Tree (Binary Indexed Tree) の実装と React フック。
 *
 * @module useFenwickMapTree
 * @description This module provides a FenwickTree (Binary Indexed Tree) data structure
 * and a React hook `useFenwickMapTree` to manage it.
 * This is optimized for virtual scrolling scenarios with dynamically sized items.
 *
 * @description このモジュールは、FenwickTree (バイナリインデックスツリー) データ構造と、
 * それを管理するための React フック `useFenwickMapTree` の提供。
 * 動的なアイテムサイズを持つ仮想スクロールのシナリオに最適化されている。
 */
import { useMemo, useRef } from "react"
import { minmax } from "./utils.ts"

type MaterializeRange = { from: number; to: number }
type MaterializeOption = { materialize: boolean; ranges?: MaterializeRange[] }
type MaterializeConfig = { materializeOption?: MaterializeOption }
type DeltaUpdate = { index: number; change: number }
type ValueUpdate = { index: number; value: number }

/**
 * @class FenwickTree
 * @classdesc Implements a Fenwick Tree (or Binary Indexed Tree).
 * This data structure efficiently calculates prefix sums and performs updates in logarithmic time.
 * It is particularly useful for virtual scrolling, where it can manage the offsets of variably sized items.
 *
 * @classdesc Fenwick Tree (バイナリインデックスツリー) の実装。
 * このデータ構造は、接頭辞和の計算と更新を対数時間で効率的に実行。
 * 可変サイズのアイテムのオフセットを管理できるため、特に仮想スクロールで有用。
 */
export class FenwickMapTree {
    /**
     * @private
     * @property {Map<number, number>} tree - The Map storing the Fenwick tree structure, specifically the sums of deltas. It is 1-indexed.
     * @property {Map<number, number>} tree - Fenwick Tree 構造を格納する Map。特に差分の合計を保持する。1-indexed。
     */
    private tree!: Map<number, number>

    /**
     * @private
     * @property {Map<number, number>} deltas - The Map storing the differences (deltas) from the base value at each index.
     * @property {Map<number, number>} deltas - 各インデックスにおける基準値との差分 (delta) を格納する Map。
     */
    private deltas!: Map<number, number>

    /**
     * @private
     * @property {number} size - The number of elements the tree manages.
     * @property {number} size - ツリーが管理する要素数。
     */
    private size!: number

    /**
     * @private
     * @property {number} baseValue - The uniform base value for all elements, used to optimize memory.
     * @property {number} baseValue - 全要素の均一な基準値。メモリ最適化のために使用。
     */
    private baseValue!: number

    /**
     * @private
     * @property {((index: number) => number) | undefined} valueFn - A function to generate values, stored for lazy initialization.
     * @property {((index: number) => number) | undefined} valueFn - 値を生成するための関数。遅延初期化のために保存される。
     */
    private valueFn?: (index: number) => number
    private total?: number

    /**
     * @constructor
     * @description Initializes the Fenwick Tree.
     * @description Fenwick Tree の初期化。
     * @param {number} size - The total number of items.
     * @param {number | ((index: number) => number)} valueOrFn - The value for all elements, or a function to generate values.
     * @param {{ sampleRange?: { from: number; to: number }, materialize?: boolean }} [options] - Optional settings for initialization.
     */
    constructor(size: number, valueOrFn: number | ((index: number) => number), options?: { sampleRange?: { from: number; to: number }; materialize?: boolean }) {
        this.reset(size, valueOrFn, options)
    }

    /**
     * @method reset
     * @description Resets the Fenwick Tree with a new size and initial values.
     * @description Fenwick Tree を新しいサイズと初期値でリセット。
     * @param {number} size - The total number of items.
     * @param {number | ((index: number) => number)} valueOrFn - The value for all elements, or a function to generate values.
     * @param {{ sampleRange?: { from: number; to: number }, materialize?: boolean }} [options] - Optional settings for initialization.
     */
    reset(size: number, valueOrFn: number | ((index: number) => number), options?: { sampleRange?: { from: number; to: number }; materialize?: boolean }) {
        this.size = size
        this.tree = new Map()
        this.deltas = new Map()
        this.total = undefined

        const isFn = typeof valueOrFn === "function"
        if (isFn) {
            this.valueFn = valueOrFn
            if (this.size > 0) {
                // サンプリング範囲を決定する
                const range = options?.sampleRange ?? {
                    from: 0,
                    to: Math.min(99, this.size - 1),
                }
                // 最頻値と生成された値を取得する
                const { mode, materializedValues } = this._calculateMode(range.from, range.to)
                this.baseValue = mode

                // サンプリング範囲の値を具現化する
                if (options?.materialize) {
                    for (let i = 0; i < materializedValues.length; i++) {
                        const value = materializedValues[i]
                        const index = range.from + i
                        if (index >= this.size) {
                            break
                        }
                        // _materialize を直接呼ぶ代わりに、計算済みの値を使って更新する
                        const change = value - this.baseValue
                        this.deltas.set(index, change)
                        this._updateTree(index, change)
                    }
                }
            } else {
                this.baseValue = 0
            }
            // 具現化が完了した後に total を計算する
            this.total = this.getTotal()
        } else {
            this.valueFn = undefined
            this.baseValue = valueOrFn
            this.total = this.baseValue * this.size
        }
    }

    /**
     * @method setValueFn
     * @description Updates the value function and re-initializes the tree.
     * @description 値関数を更新し、ツリーを再初期化する。
     * @param {number | ((index: number) => number)} valueOrFn - The new value for all elements, or a function to generate values.
     */
    setValueFn(valueOrFn: number | ((index: number) => number)) {
        if (typeof valueOrFn === "function") {
            this.valueFn = valueOrFn
        } else {
            // If a number is provided, it's treated as a new baseValue,
            // and valueFn is cleared. This case effectively turns the tree
            // into a uniform-value tree, but existing deltas are preserved.
            this.valueFn = undefined
            this.baseValue = valueOrFn
        }
    }

    /**
     * @private
     * @method _calculateMode
     * @description Calculates the mode (most frequent value) within a given range of values generated by `valueFn`. If multiple modes exist, it returns their average. If no value appears more than once, it returns the median of the range. This approach helps determine a representative `baseValue` for the Fenwick tree, especially for data with high variance. It also returns the array of values generated within the range for reuse.
     * @description `valueFn` によって生成される値の指定された範囲内の最頻値を計算する。最頻値が複数存在する場合は、それらの平均値を返す。どの値も複数回出現しない場合は、範囲の中央値を返す。このアプローチは、特に分散の大きいデータに対して、Fenwick Tree の代表的な `baseValue` を決定するのに役立つ。また、再利用のために範囲内で生成された値の配列も返す。
     * @param {number} from - The starting index of the range (inclusive).
     * @param {number} to - The ending index of the range (inclusive).
     * @returns {{ mode: number; materializedValues: number[] }} An object containing the calculated mode and the array of generated values.
     */
    private _calculateMode(from: number, to: number): { mode: number; materializedValues: number[] } {
        if (!this.valueFn) {
            return { mode: 0, materializedValues: [] }
        }

        const values: number[] = []
        for (let i = from; i <= to; i++) {
            if (i >= this.size) {
                break
            }
            values.push(this.valueFn(i))
        }
        // valueFn で生成した値を再利用するためにコピーを保持する
        const materializedValues = [...values]

        if (values.length === 0) {
            return { mode: 0, materializedValues: [] }
        }

        // 中央値を計算してデフォルトの最頻値として設定
        values.sort((a, b) => a - b)
        const mid = Math.floor(values.length / 2)
        let mode: number
        if (values.length % 2 === 0) {
            // 偶数個の場合は中央2つの値の平均
            mode = Math.floor((values[mid - 1] + values[mid]) / 2)
        } else {
            // 奇数個の場合は中央の値
            mode = values[mid]
        }

        const frequencies = new Map<number, number>()
        let maxFreq = 0

        for (const value of values) {
            const count = (frequencies.get(value) ?? 0) + 1
            frequencies.set(value, count)
            if (count > maxFreq) {
                maxFreq = count
            }
        }

        if (maxFreq > 1) {
            const modes: number[] = []
            for (const [value, count] of frequencies.entries()) {
                if (count === maxFreq) {
                    modes.push(value)
                }
            }
            const sum = modes.reduce((a, b) => a + b, 0)
            mode = Math.floor(sum / modes.length)
        }
        return { mode, materializedValues }
    }

    /**
     * @method update
     * @description Updates the value at a given index.
     * @description 指定されたインデックスの値を更新。
     * @param {number} index - The 0-based index to update.
     * @param {number} value - The new value.
     */
    update(index: number, value: number): number | undefined {
        return this.updates([{ index, value }])
    }

    /**
     * @method updates
     * @description Updates the values at given indices.
     * @description 指定されたインデックスの値を更新。
    * @param {ValueUpdate[]} updates - An array of updates, each with an index and the new value.
     */
    updates(updates: ValueUpdate[]): number | undefined {
        const deltaUpdates = this._buildDeltaUpdates(updates)
        if (deltaUpdates.length > 0) {
            return this.updateDeltas(deltaUpdates)
        }
        return this.total
    }

    /**
     * @method updateDelta
     * @description Updates the delta at a given index and propagates the change through the tree.
     * @description 指定されたインデックスのデルタを更新し、変更をツリーに伝播させる。
     * @param {number} index - The 0-based index to update.
     * @param {number} change - The value to add to the delta at the given index.
     */
    updateDelta(index: number, change: number): number | undefined {
        return this.updateDeltas([{ index, change }])
    }

    /**
     * @method updateDeltas
     * @description Updates the deltas at given indices and propagates the changes through the tree.
     * @description 指定されたインデックスのデルタを更新し、変更をツリーに伝播させる。
    * @param {DeltaUpdate[]} updates - An array of updates, each with an index and the change to apply.
     */
    updateDeltas(updates: DeltaUpdate[]): number | undefined {
        for (const { index, change } of updates) {
            if (index < 0 || index >= this.size) {
                throw new Error(`Index ${index} out of bounds`)
            }

            // deltas Map を更新
            const currentDelta = this.deltas.get(index) ?? 0
            this.deltas.set(index, currentDelta + change)

            // ツリーを更新
            this._updateTree(index, change)
        }

        return this.total
    }

    /**
     * @private
     * @method _updateTree
     * @description Updates the Fenwick tree and the total sum with a given change.
     * @description Fenwick Tree と合計値を指定された変更で更新する。
     * @param {number} index - The 0-based index that changed.
     * @param {number} change - The change in value.
     */
    private _updateTree(index: number, change: number) {
        if (change === 0) {
            return
        }
        // tree Map を更新
        let treeIndex = index + 1 // Fenwick Tree のアルゴリズムは 1 始まりで設計される
        while (treeIndex <= this.size) {
            this.tree.set(treeIndex, (this.tree.get(treeIndex) ?? 0) + change)
            treeIndex += treeIndex & -treeIndex
        }

        // 合計値を更新 (totalが計算済みの場合のみ)
        if (this.total !== undefined) {
            this.total += change
        }
    }

    /**
     * @private
     * @method _buildDeltaUpdates
     * @description Converts value updates into delta updates while preserving validation logic.
     * @description 値更新入力を検証しつつデルタ更新へ変換する。
     * @param {ValueUpdate[]} updates - Requested value updates.
     * @returns {DeltaUpdate[]} Sanitized delta updates ready to apply.
     */
    private _buildDeltaUpdates(updates: ValueUpdate[]): DeltaUpdate[] {
        const deltaUpdates: DeltaUpdate[] = []
        for (const { index, value } of updates) {
            if (index < 0 || index >= this.size) {
                throw new Error(`Index ${index} out of bounds`)
            }
            if (value < 0) {
                throw new Error("Value cannot be negative.")
            }

            // 現在値を取得して差分を算出
            const oldValue = this.deltas.has(index) ? (this.deltas.get(index) ?? 0) + this.baseValue : this.baseValue
            const change = value - oldValue
            if (change !== 0) {
                deltaUpdates.push({ index, change })
            }
        }
        return deltaUpdates
    }

    /**
     * @private
     * @method _materialize
     * @description Materializes the value at a specific index if it hasn't been already.
     * @description 特定のインデックスの値がまだ具現化されていない場合に具現化する。
     * @param {number} index - The 0-based index to materialize.
     * @param {boolean} [updateTree=true] - Whether to update the Fenwick tree after materialization.
     */
    private _materialize(index: number, updateTree = true) {
        if (this.valueFn) {
            // 1. 現在の差分を取得する (存在しなければ 0)
            const oldDelta = this.deltas.get(index) ?? 0

            // 2. valueFn から本来の値を計算し、新しい差分を求める
            const value = this.valueFn(index)
            const newDelta = value - this.baseValue

            // 3. 差分が実際に変わった場合のみ、ツリーを更新する
            if (newDelta !== oldDelta) {
                this.deltas.set(index, newDelta)
                if (updateTree) {
                    // ツリーに反映すべき差分は「新しい差分」と「古い差分」の間の差
                    const changeForTree = newDelta - oldDelta
                    this._updateTree(index, changeForTree)
                }
            }
        }
    }

    /**
     * @private
     * @method _materializeRanges
     * @description Materializes values for provided ranges and optionally a target index, keeping existing semantics for each caller.
     * @description 指定された範囲とターゲットインデックスを既存仕様通りに具現化する。
     * @param {MaterializeOption | undefined} option - Materialization option wrapper.
     * @param {number | undefined} index - Target index for materialization.
     * @param {boolean} [forceIndex=false] - When true, materializes the index even if it is outside the provided ranges.
     */
    private _materializeRanges(option?: MaterializeOption, index?: number, forceIndex = false) {
        if (!(option?.materialize && this.valueFn)) {
            return
        }

        const ranges = option.ranges
        if (ranges && ranges.length > 0) {
            for (const range of ranges) {
                const from = range.from
                const to = Math.min(range.to, this.size - 1)
                for (let i = from; i <= to; i++) {
                    this._materialize(i)
                }
            }

            if (index === undefined) {
                return
            }

            if (forceIndex) {
                this._materialize(index)
                return
            }

            const first = ranges[0].from
            const last = ranges[ranges.length - 1].to
            if (index >= first && index <= last) {
                this._materialize(index)
            }
            return
        }

        if (index !== undefined) {
            this._materialize(index)
        }
    }

    /**
     * @private
     * @method _findIndex
     * @description Executes a binary search over prefix sums to find the first index satisfying a boundary condition.
     * @description 累積和に対する二分探索を行い、境界条件を満たす最初のインデックスを求める。
     * @param {number} target - Target cumulative value.
     * @param {MaterializeConfig} options - Materialization setting wrapper.
     * @param {boolean} chooseLowerBound - When true, finds the smallest index meeting or exceeding the target; otherwise finds the largest index not exceeding it.
     * @returns {{ index: number; total: number | undefined; cumulative: number | undefined; currentValue: number | undefined; safeIndex: number | undefined }} Binary search result.
     */
    private _findIndex(
        target: number,
        options: MaterializeConfig = {},
        chooseLowerBound: boolean,
    ): { index: number; total: number | undefined; cumulative: number | undefined; currentValue: number | undefined; safeIndex: number | undefined } {
        if (this.size === 0) {
            return { index: -1, total: this.total ?? 0, cumulative: undefined, currentValue: undefined, safeIndex: undefined }
        }

        let low = 0
        let high = this.size - 1
        let ans = -1
        let result:
            | {
                  cumulative: number
                  total: number | undefined
                  currentValue: number
                  safeIndex: number
              }
            | undefined
        let finalTotal = this.total

        while (low <= high) {
            const mid = Math.floor((low + high) / 2)
            result = this.prefixSum(mid, options)
            finalTotal = result.total
            const meetsCondition = chooseLowerBound ? result.cumulative >= target : result.cumulative <= target
            if (meetsCondition) {
                ans = mid
                if (chooseLowerBound) {
                    high = mid - 1
                } else {
                    low = mid + 1
                }
            } else if (chooseLowerBound) {
                low = mid + 1
            } else {
                high = mid - 1
            }
        }

        return { index: ans, total: finalTotal, cumulative: result?.cumulative, currentValue: result?.currentValue, safeIndex: result?.safeIndex }
    }

    /**
     * @method prefixSum
     * @description Calculates the cumulative sum up to a given index (inclusive) in O(log n) time.
     * @description 指定されたインデックスまでの累積和を O(log n) で計算。
     * @param {number} index - The 0-based index to prefixSum up to.
     * @param {MaterializeConfig} [options] - Optional settings for materializing values.
     * @returns {{ cumulative: number; total: number | undefined; currentValue: number; safeIndex: number }} The cumulative sum of values from index 0 to the given index, the total sum, and the value at the given index.
     */
    prefixSum(index: number, options?: MaterializeConfig): { cumulative: number; total: number | undefined; currentValue: number; safeIndex: number } {
        if (index < 0) {
            return { cumulative: 0, total: this.total, currentValue: 0, safeIndex: 0 }
        }
        const safeIndex = minmax(index, 0, this.size - 1)

        const materializeOption = options?.materializeOption
        this._materializeRanges(materializeOption, safeIndex, true)

        let sum = 0
        let treeIndex = safeIndex + 1 // Fenwick Tree のアルゴリズムは 1 始まりで設計される
        while (treeIndex > 0) {
            const treeNodeValue = this.tree.get(treeIndex) ?? 0
            sum += treeNodeValue
            treeIndex -= treeIndex & -treeIndex
        }

        const currentValue = materializeOption?.materialize ? this.get(safeIndex) : (this.deltas.get(safeIndex) || 0) + this.baseValue

        // ベース値に基づく合計を加算
        return { cumulative: sum + this.baseValue * (safeIndex + 1), total: this.total, currentValue, safeIndex }
    }

    /**
     * @method get
     * @description Gets the value at a specific index.
     * @description 特定のインデックスの値を取得。
     * @param {number} index - The 0-based index to get.
     * @param {MaterializeConfig} [options] - Optional settings for materializing values.
     * @returns {number} The value at the given index.
     */
    get(index: number, options?: MaterializeConfig): number {
        if (index < 0 || index >= this.size) {
            throw new Error("Index out of bounds")
        }

        const materializeOption = options?.materializeOption
        this._materializeRanges(materializeOption, index)

        return (this.deltas.get(index) ?? 0) + this.baseValue
    }

    /**
     * @method getTotal
     * @description Gets the total sum of all values in the tree.
     * @description ツリー内のすべての値の合計を取得。
     * @param {MaterializeConfig} [options] - Optional settings for materializing values.
     * @returns {number} The total sum of all values.
     */
    getTotal(options?: MaterializeConfig): number {
        const materializeOption = options?.materializeOption
        this._materializeRanges(materializeOption)

        if (this.total === undefined) {
            if (this.size === 0) {
                this.total = 0
            } else {
                // 具現化は冒頭の処理に任せて高さを計算する
                let total = this.baseValue * this.size
                for (const delta of this.deltas.values()) {
                    total += delta
                }
                this.total = total
                const lastPrefix = this.prefixSum(this.getSize() - 1)
                console.assert(lastPrefix.cumulative === lastPrefix.total, "Inconsistent Fenwick Tree state")
            }
        }

        return this.total
    }

    /**
     * @method rebuildTree
     * @description Rebuilds the Fenwick Tree from the existing `baseValue` and `deltas`. This corrects any discrepancies in the tree's internal state, such as those caused by floating-point errors, by recalculating the tree structure and the total sum from the source `deltas`. This method does not re-materialize values from `valueFn`.
     * @description 既存の `baseValue` と `deltas` から Fenwick Tree を再構築します。これにより、`deltas` からツリー構造と合計値を再計算することで、浮動小数点誤差などによって生じた内部状態の不一致を修正します。このメソッドは `valueFn` から値を再具現化しません。
     * @param {object} [options] - Optional settings for rebuilding.
     * @param {boolean} [options.materialize=false] - If true and `valueFn` is provided, re-materializes all values, recalculating `deltas` and `baseValue`.
     */
    rebuildTree(options?: { materialize?: boolean }) {
        if (options?.materialize && this.valueFn) {
            // すべての値を具現化する
            const valueFn = this.valueFn
            this.reset(this.size, (i) => valueFn(i), { materialize: true })
            return
        }

        const newTree = new Map<number, number>()
        let newTotal = this.baseValue * this.size

        // 既存の deltas を使って新しいツリーを構築し、合計値も同時に再計算する
        for (const [index, delta] of this.deltas.entries()) {
            newTotal += delta

            if (delta === 0) {
                continue
            }
            // Fenwick Tree のアルゴリズムは 1 始まりで設計されるため、インデックスを 1 加算する
            let treeIndex = index + 1
            while (treeIndex <= this.size) {
                newTree.set(treeIndex, (newTree.get(treeIndex) ?? 0) + delta)
                treeIndex += treeIndex & -treeIndex
            }
        }

        // 最後に状態をアトミックに更新
        this.tree = newTree
        this.total = newTotal
    }

    /**
     * @method calculateAccumulatedError
     * @description Compares the cached total sum with a theoretical total calculated directly from the source values (`deltas` and `baseValue`, or `valueFn`). This helps detect any discrepancy in the tree's cached state, which might be caused by floating-point errors or other inconsistencies.
     * @description キャッシュされている合計値と、元の値 (`deltas` と `baseValue`、または `valueFn`) から直接計算した理論上の合計値とを比較します。これにより、浮動小数点数の累積誤差やその他の不整合によって生じる可能性のある、ツリーのキャッシュ状態の不一致を検出できます。
     * @returns {number} The difference between the cached total and the theoretical total.
     */
    calculateAccumulatedError(): number {
        if (this.total === undefined) {
            // total がまだ計算されていない場合は、誤差は 0 とする
            return 0
        }

        // 理論上の合計値を計算
        let theoreticalTotal = this.baseValue * this.size
        for (const delta of this.deltas.values()) {
            theoreticalTotal += delta
        }

        // キャッシュされている合計値との差を返す
        return this.total - theoreticalTotal
    }

    /**
     * @method changeSize
     * @description Changes the size of the Fenwick Tree.
     * @description Fenwick Tree のサイズを変更する。
     * @param {number} newSize - The new size of the tree.
     */
    changeSize(newSize: number) {
        const oldSize = this.size
        if (newSize === oldSize) {
            // サイズが変わらない場合は何もしない
            return
        }

        // サイズが小さくなる場合、範囲外の delta を削除する
        if (newSize < oldSize) {
            for (const index of this.deltas.keys()) {
                if (index >= newSize) {
                    this.deltas.delete(index)
                }
            }
        }

        this.size = newSize
        // サイズ変更後、deltas に基づいてツリー全体を再構築します。
        this.rebuildTree()

    const lastPrefix = this.prefixSum(this.getSize() - 1)
    console.assert(lastPrefix.cumulative === lastPrefix.total, "Inconsistent Fenwick Tree state")
    }

    /**
     * @method getSize
     * @description Gets the size of the tree.
     * @description ツリーのサイズを取得。
     * @returns {number} The total number of items.
     */
    getSize(): number {
        return this.size
    }

    /**
     * @method findIndexAtOrAfter
     * @description Finds the first index where the cumulative sum is greater than or equal to a target value.
     * @description 累積和がターゲット値以上になる最初のインデックスを検索。
     * @param {number} target - The target cumulative sum.
    * @param {MaterializeConfig} [options] - Optional settings for materializing values.
    * @param {MaterializeOption} [options.materializeOption] - Options to control materialization.
    * @param {boolean} [options.materializeOption.materialize=false] - Whether to materialize values.
    * @param {MaterializeRange[]} [options.materializeOption.ranges] - Optional ranges for eager materialization.
     * @returns {{ index: number, total: number | undefined, cumulative: number | undefined, currentValue: number | undefined, safeIndex: number | undefined }} The 0-based index and the total sum, or -1 if not found.
     */
    findIndexAtOrAfter(
        target: number,
        options?: MaterializeConfig,
    ): { index: number; total: number | undefined; cumulative: number | undefined; currentValue: number | undefined; safeIndex: number | undefined } {
        return this._findIndex(target, options ?? {}, true)
    }

    /**
     * @method findIndexAtOrBefore
     * @description Finds the last index where the cumulative sum is less than or equal to a target value.
     * @description 累積和がターゲット値以下になる最後のインデックスを検索。
     * @param {number} target - The target cumulative sum.
    * @param {MaterializeConfig} [options] - Optional settings for materializing values.
    * @param {MaterializeOption} [options.materializeOption] - Options to control materialization。
    * @param {boolean} [options.materializeOption.materialize=false] - Whether to materialize values。
    * @param {MaterializeRange[]} [options.materializeOption.ranges] - Optional ranges for eager materialization。
     * @returns {{ index: number, total: number | undefined, cumulative: number | undefined, currentValue: number | undefined, safeIndex: number | undefined }} The 0-based index and the total sum, or -1 if not found.
     */
    findIndexAtOrBefore(
        target: number,
        options?: MaterializeConfig,
    ): { index: number; total: number | undefined; cumulative: number | undefined; currentValue: number | undefined; safeIndex: number | undefined } {
        return this._findIndex(target, options ?? {}, false)
    }
}

/**
 * @hook useFenwickMapTree
 * @description A React hook that creates and manages a `FenwickMapTree` instance.
 * The tree instance is stable across re-renders. A new tree instance is created
 * if `size` or `valueOrFn` changes.
 * @description `FenwickMapTree` インスタンスを作成・管理する React フック。
 * ツリーインスタンスは再レンダリングされても同一性を維持する。`size` または `valueOrFn` が
 * 変更された場合に新しいインスタンスが作成される。
 * @param {number} size - The total number of items.
 * @param {number | ((index: number) => number)} valueOrFn - The value for all elements, or a function to generate values.
 * @param {number | ((index: number) => number)} valueOrFn - 全要素の均一な値、または値を生成する関数。不要なツリーの再作成を防ぐため、この関数は `useCallback` でメモ化すること。
 * @param {{ sampleRange?: { from: number; to: number } }} [options] - Optional settings for initialization. To prevent unnecessary re-creations of the tree, memoize this object with `useMemo`.
 * @param {{ sampleRange?: { from: number; to: number } }} [options] - 初期化時のオプション設定。不要なツリーの再作成を防ぐため、このオブジェクトは `useMemo` でメモ化すること。
 * @returns {FenwickMapTree} The FenwickMapTree instance.
 * @returns {FenwickMapTree} FenwickMapTree インスタンス。
 */
export const useFenwickMapTree = (size: number, valueOrFn: number | ((index: number) => number), options?: { sampleRange?: { from: number; to: number } }): FenwickMapTree => {
    const validSize = Math.max(0, size)
    const prevTreeRef = useRef<FenwickMapTree | null>(null)

    const tree = useMemo(() => {
        const newTree = new FenwickMapTree(validSize, valueOrFn, options)
        return newTree
    }, [validSize, valueOrFn, options])

    // インスタンスが更新された場合にログを出力
    if (!Object.is(prevTreeRef.current, tree)) {
        console.warn("[useFenwickMapTree] instance changed")
    }
    prevTreeRef.current = tree

    return tree
}
